import { TierIIReportKind } from "encamp-prisma/generated/client/client";
import { TabProxy } from "encampy";
import {
  AlternateId,
  PortalCredential,
  PortalTech,
} from "generated-graphql/graphql";
import { automateAZmyDEQLogin } from "./az";
import { automateCERSLogin } from "./ca";
import { automateEPlanLogin } from "./eplan";
import { automateKSTier2OnlineLogin } from "./ks";
import { automateMOLogin } from "./mo";
import { automateNJLogin } from "./nj";
import { automateSDLogin } from "./sd";
import { automateTierIIManagerLogin } from "./tieriimanager";
import { automateTxLogin } from "./tx";
import { automateWALogin } from "./wa";

export function getEncampyLoginAutomation(
  portalCredential: Pick<
    PortalCredential,
    "type" | "url" | "credential" | "tech"
  >,
  facilityAlternateIds?: AlternateId[] | null,
  reportingYear?: number,
  reportKind?: TierIIReportKind
): (tab: TabProxy) => Promise<void> {
  if (!portalCredential.type) {
    return () => {
      throw new Error("Portal type is required for login automation");
    };
  }

  if (!portalCredential.url) {
    return () => {
      throw new Error("Portal URL is required for login automation");
    };
  }

  if (
    !portalCredential.credential?.username ||
    !portalCredential.credential?.password
  ) {
    return () => {
      throw new Error(
        "Username and password are required for login automation"
      );
    };
  }

  if (
    portalCredential.tech === "TX" &&
    !portalCredential.credential?.securityQuestions
  ) {
    return () => {
      throw new Error(
        "Security questions are required for Texas portal login automation"
      );
    };
  }

  const { username, password, securityQuestions } = portalCredential.credential;

  switch (portalCredential.tech) {
    case "AZmyDEQ":
    case "AZmyDeq":
      return (tab: TabProxy) => automateAZmyDEQLogin(tab, username, password);

    case "CERS":
    case "Cers":
      return (tab: TabProxy) =>
        automateCERSLogin({
          tab,
          username,
          password,
          facilityAlternateIds: facilityAlternateIds ?? [],
        });

    case "EPlan":
      return (tab: TabProxy) => automateEPlanLogin(tab, username, password);

    case "KS":
    case "Ks":
      return (tab: TabProxy) =>
        automateKSTier2OnlineLogin(tab, username, password);

    case "MO":
    case "Mo":
      return (tab: TabProxy) => automateMOLogin(tab, username, password);

    case PortalTech.Nj:
      return (tab: TabProxy) => automateNJLogin(tab, username, password);

    case PortalTech.Sd:
      return (tab: TabProxy) => automateSDLogin(tab, username, password);

    case PortalTech.Tx:
      return (tab: TabProxy) =>
        automateTxLogin({
          tab,
          username,
          password,
          facilityAlternateIds: facilityAlternateIds ?? [],
          securityQuestions: securityQuestions ?? [],
          reportingYear,
          reportKind,
        });

    case "Tier2Manager":
      return (tab: TabProxy) =>
        automateTierIIManagerLogin(tab, username, password);

    case PortalTech.Wa:
      return (tab: TabProxy) => automateWALogin(tab, username, password);

    // Add cases for other portal types as needed
    default:
      return () => {
        throw new Error(
          `Login automation not implemented for portal type: ${portalCredential.type}`
        );
      };
  }
}
